import { Component, DestroyRef, inject, Input, OnInit, computed } from '@angular/core';
import { Account, User, UserManagementColumn, UsersBrands } from '../types';
import { AgGridAngular } from 'ag-grid-angular';
import { ClientSideRowModelModule, GridOptions, RowClickedEvent, themeQuartz, type ColDef, SortChangedEvent } from "ag-grid-community";
import { MatDialog } from '@angular/material/dialog';
import { AgTooltipCellComponent } from '../../../shared/components/ag-tooltip-cell/ag-tooltip-cell.component';
import { UserModalComponent } from '../user-modal/user-modal.component';
import { StateService } from '../../../services/state.service';
import { AuthorizationService } from '../../../services/authorization.service';
import { capitalizeAllFirstLetters } from '../../../utils/strings';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UtilService } from '../../../services/util.service';
import { ManagementService } from '../management.service';
import {getManagementDateFormat} from '../../../utils/time';


@Component({
  selector: 'app-users-table',
  imports: [AgGridAngular],
  templateUrl: './users-table.component.html',
  styleUrl: './users-table.component.scss'
})
export class UsersTableComponent {
  data = computed(() => this.managementService.tableData() as User[]);
  colDefs: ColDef[] = this.getColDef();
  theme = themeQuartz.withParams({
    spacing: 10,
    headerBackgroundColor: 'rgba(217, 217, 217, 0.3)',
    rowHoverColor: 'rgba(233, 244, 245, 0.85)',
    fontSize: '14px',
    headerFontSize: '16px',
    fontFamily: 'Poppins',
    headerFontFamily: 'Poppins',
    headerFontWeight: 600,
    headerTextColor: '#717172;',
    cellTextColor: '#717172;',
    headerHeight: 64,
  });
  modules = [ClientSideRowModelModule];
  gridOptions: GridOptions = {
    suppressMovableColumns: true,
    domLayout: 'autoHeight',
    rowHeight: undefined,
    defaultColDef: {
      resizable: false,
      autoHeight: true,
      sortable: true,
      wrapText: true,
      flex: 1
    },
  };
  private destroyRef = inject(DestroyRef);

  constructor(private dialog: MatDialog, private stateService: StateService,
    private authorizationService: AuthorizationService, private utilService: UtilService,
    private managementService: ManagementService) { }

  onRowClicked = (event: RowClickedEvent) => {
    const dialogRef = this.dialog.open(UserModalComponent, {
      data: {
        ...event.data,
        brandKeys: event.data.brands.map((brand: UsersBrands) => brand.key)
      },
      panelClass: 'management-modal',
      autoFocus: false
    });
    dialogRef.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((result: User) => {
      if (result) {
        const currentData = this.data();
        const index = currentData.findIndex(user => user.id === result.id);
        if (index !== -1) {
          const updated = [...currentData];
          updated[index] = result;
          this.managementService.tableData.set(updated);
          if (this.authorizationService.userProfile.email === result.email) {
            this.authorizationService.userProfile = {
              email: result.email,
              firstName: capitalizeAllFirstLetters(result.firstName),
              lastName: capitalizeAllFirstLetters(result.lastName)
            };
          }
          this.stateService.updateAccountsOnUserUpdate(result.account, result.email)
        }
        this.utilService.openSnackBar(`User "${result.firstName}" updated successfuly`, { position: 'center', variant: 'success' });
      }
      this.stateService.loading = false;
    });
  };

  public onSortChanged(event: SortChangedEvent) {
    const sortModel = event.columns?.[0]
    this.managementService.sortState.set({
      sortBy: sortModel?.getColId()|| '',
      sortDir: sortModel?.getSort() || 'desc'
    });
    this.managementService.refreshTable$.next();
  }

  private getColDef() {
    return [
      {
        field: UserManagementColumn.EMAIL,
        cellRenderer: AgTooltipCellComponent,
        flex: 1.5,
      },
      {
        field: UserManagementColumn.FIRST_NAME,
        valueGetter: (params: any) => {
          return params.data[UserManagementColumn.FIRST_NAME] ? capitalizeAllFirstLetters(params.data[UserManagementColumn.FIRST_NAME]) : '';
        }
      },
      {
        field: UserManagementColumn.LAST_NAME,
        valueGetter: (params: any) => {
          return params.data[UserManagementColumn.LAST_NAME] ? capitalizeAllFirstLetters(params.data[UserManagementColumn.LAST_NAME]) : '';
        }
      },
      {
        field: UserManagementColumn.ROLE,
        sortable: false,
      },
      {
        field: UserManagementColumn.CONNECTED_BRANDS,
        cellRenderer: AgTooltipCellComponent,
        valueGetter: (params: any) => {
          return params.data[UserManagementColumn.CONNECTED_BRANDS]
            .map((item: UsersBrands) => item?.name)
            .filter(Boolean)
            .join(', ');
        },
        sortable: false,
      },
      {
        field: UserManagementColumn.ACCOUNT,
        cellRenderer: AgTooltipCellComponent,
        valueGetter: (params: any) => {
          return params.data[UserManagementColumn.ACCOUNT]?.name
        },
        sortable: false,
      },
      {
        field: UserManagementColumn.CREATED_AT,
        cellRenderer: AgTooltipCellComponent,
        valueGetter: (params: any) => {
          return getManagementDateFormat(params.data[UserManagementColumn.CREATED_AT])
        }
      },
      {
        field: UserManagementColumn.LAST_LOGIN,
        cellRenderer: AgTooltipCellComponent,
        valueGetter: (params: any) => {
          return params.data[UserManagementColumn.LAST_LOGIN] ? getManagementDateFormat(params.data[UserManagementColumn.LAST_LOGIN]) : ''
        },
        sortable: false,
      },
    ];
  }
}
